import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EXPORT_FORMATS, buildFilename, runExport } from './exportRows';
import { toast } from '../Toast/toast';
import './export-dialog.css';

/**
 * THE EXPORT POPUP -- one dialog for every Export button in the app.
 *
 * The button used to be the whole interaction: click it and a file
 * appeared in the downloads tray, or did not, with nothing said either
 * way and no way to know what was in it. A list of 40 columns exported
 * all 40; a server-paged list exported the 20 rows that happened to be
 * on screen without admitting it.
 *
 * So the dialog answers the three questions the button could not: HOW
 * MANY rows and which ones, WHICH COLUMNS, and WHAT FORMAT. It says the
 * file name before it writes it. Nothing else -- it is a confirm step,
 * not a report builder.
 *
 * It is portalled to <body> like the toast host. The dialog opens from
 * a component that sits several transformed ancestors deep on 27
 * different pages, and `position: fixed` is measured from the nearest
 * transformed ancestor rather than the viewport.
 */

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

// Built once. As a default parameter it would be a new array on every
// render, which is a new identity for the memo below and for any effect
// that depends on it.
const ALL_FORMAT_IDS = EXPORT_FORMATS.map((f) => f.id);

const ExportDialog = ({
  open,
  onClose,
  /** What is being exported -- "Batches", "Student attendance". */
  title = 'Data',
  /** [{ key, label, value }] -- see exportRows.js. */
  columns = [],
  rows = [],
  /** One line under the count: which rows these are. */
  scopeNote,
  /** Base for the file name; the date and extension are added. */
  filenameBase,
  sheetName,
  /** Whole lines written above and below the table. */
  preamble,
  appendix,
  /** Format ids to offer. Both by default. */
  formats = ALL_FORMAT_IDS,
  /** Called with { filename, count, format } after a successful write. */
  onExported,
}) => {
  const [format, setFormat] = useState(formats[0] || 'csv');
  const [chosen, setChosen] = useState(() => columns.map((c) => c.key));
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const offered = useMemo(
    () => EXPORT_FORMATS.filter((f) => formats.includes(f.id)),
    [formats],
  );

  // The signature, not the array: a parent that builds its columns
  // inline hands over a new array on every render, and keying the reset
  // on the array itself would clear the reader's ticks as they clicked.
  const columnSignature = columns.map((c) => c.key).join('|');

  // A fresh dialog starts with everything ticked. Reopening after a
  // deselection should not silently export fewer columns than the list
  // shows -- the reader has to be able to see what they are getting.
  useEffect(() => {
    if (!open) return;
    setChosen(columnSignature ? columnSignature.split('|') : []);
    setBusy(false);
    setFormat(offered[0]?.id || 'csv');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, columnSignature]);

  const close = useCallback(() => {
    if (busy) return;
    onClose?.();
  }, [busy, onClose]);

  // Escape closes, and Tab cycles inside the panel. Without the trap,
  // tabbing walks out of the dialog and into the page behind it, which
  // is still there and still looks reachable.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null,
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, close]);

  // Hold the page still behind the dialog, and give focus back to the
  // Export button afterwards so the keyboard does not restart at the top
  // of the document.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const raf = requestAnimationFrame(() => panelRef.current?.focus());
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const selected = columns.filter((c) => chosen.includes(c.key));
  const rowCount = rows.length;
  const extension =
    EXPORT_FORMATS.find((f) => f.id === format)?.extension || 'csv';
  const filename = buildFilename(filenameBase || title, extension);

  const blocked =
    (rowCount === 0 && 'There is nothing on this list to export.') ||
    (selected.length === 0 && 'Pick at least one column.') ||
    null;

  // Order is the table's, not the order they were ticked in: a CSV whose
  // columns arrive in click order does not match the list it came from.
  const toggleColumn = (key) =>
    setChosen((current) =>
      current.includes(key)
        ? current.filter((k) => k !== key)
        : columns
            .filter((c) => c.key === key || current.includes(c.key))
            .map((c) => c.key),
    );

  const allChosen = selected.length === columns.length;

  const handleExport = async () => {
    if (blocked || busy) return;
    setBusy(true);
    try {
      const result = await runExport({
        format,
        columns: selected,
        rows,
        filenameBase: filenameBase || title,
        sheetName: sheetName || title,
        preamble,
        appendix,
      });
      // An export is not a change: it stays a toast, and { toast: true }
      // keeps any popup this dialog was opened over from taking it.
      toast.success(
        `${result.count} ${result.count === 1 ? 'row' : 'rows'} exported to ${result.filename}`,
        { toast: true },
      );
      onExported?.(result);
    } catch (error) {
      console.error('Export failed:', error);
      // The dialog stays open. A toast alone would take away the only
      // thing the reader can do about it, which is press it again.
      toast.error(
        error?.message
          ? `Export failed — ${error.message}`
          : 'Export failed. Nothing was downloaded.',
      );
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="xp"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="xp-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="xp-title"
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="xp-head">
          <div className="xp-heading">
            <p className="xp-eyebrow">Export</p>
            <h2 className="xp-title" id="xp-title">
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="xp-close"
            onClick={close}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="xp-body">
          {/* What is going out. Label above the figure, caption under it
              -- read as a report rather than as a poster. */}
          <div className="xp-count">
            <span className="xp-count-label">Rows to export</span>
            <span className="xp-count-value">{rowCount.toLocaleString()}</span>
            {scopeNote && <span className="xp-count-note">{scopeNote}</span>}
          </div>

          {offered.length > 1 && (
            /* role="group" rather than <fieldset>/<legend>: a floated or
               positioned <legend> leaves the fieldset's legend slot and
               becomes an ordinary box, which is the only way to sit one
               beside a "Select all" link -- and a float there overlapped
               the grid under it. This is the same thing to a screen
               reader and behaves like every other box. */
            <div className="xp-group" role="group" aria-labelledby="xp-fmt">
              <p className="xp-legend" id="xp-fmt">Format</p>
              <div className="xp-formats">
                {offered.map((option) => (
                  <label
                    key={option.id}
                    className={`xp-format${format === option.id ? ' is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="xp-format"
                      className="xp-radio"
                      value={option.id}
                      checked={format === option.id}
                      onChange={() => setFormat(option.id)}
                    />
                    <span className="xp-format-body">
                      <span className="xp-format-name">{option.label}</span>
                      <span className="xp-format-note">{option.note}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="xp-group" role="group" aria-labelledby="xp-cols">
            <div className="xp-group-head">
              <p className="xp-legend" id="xp-cols">
                Columns
                <span className="xp-legend-count">
                  {selected.length} of {columns.length}
                </span>
              </p>
              <button
                type="button"
                className="xp-link"
                onClick={() =>
                  setChosen(allChosen ? [] : columns.map((c) => c.key))
                }
              >
                {allChosen ? 'Clear all' : 'Select all'}
              </button>
            </div>

            <div className="xp-cols">
              {columns.map((column) => (
                <label className="xp-col" key={column.key}>
                  <input
                    type="checkbox"
                    className="xp-check"
                    checked={chosen.includes(column.key)}
                    onChange={() => toggleColumn(column.key)}
                  />
                  <span className="xp-col-label">{column.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <footer className="xp-foot">
          {/* The file name before it is written, not after. It is the
              only way to tell two exports apart in the downloads tray. */}
          <p className="xp-file">
            {blocked || (
              <>
                Saves as <span className="xp-file-name">{filename}</span>
              </>
            )}
          </p>
          <div className="xp-foot-acts">
            <button
              type="button"
              className="xp-btn"
              onClick={close}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="xp-btn xp-btn--main"
              onClick={handleExport}
              disabled={!!blocked || busy}
            >
              {busy && <span className="xp-spinner" aria-hidden="true" />}
              {busy ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default ExportDialog;
